import { useState, useEffect, useCallback } from 'react'
import cutileiApi from 'services/cutileiApi'
import BankingService from 'services/Banking'
import { groupByDate, formatGroupDate } from '../dateGroups'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import Select from 'components/Inputs/Select'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { MdPix } from 'react-icons/md'
import { FaBarcode, FaUniversity, FaExchangeAlt, FaHandHoldingUsd, FaReceipt, FaArrowDown, FaArrowUp, FaSyncAlt } from 'react-icons/fa'
import { DateTime } from 'luxon'

import {
  Card,
  CardHeader,
  CardTitle,
  TransactionList,
  TransactionRow,
  TransactionIcon,
  TransactionInfo,
  TransactionMeta,
  TransactionSide,
  DateGroupLabel,
  StatusBadge,
  EmptyState,
  AmountText,
  Row,
  FormField,
  WarningText,
  Caption,
  InfoText
} from '../styles'

const PER_PAGE = 20

const OPERATION_ICONS = {
  pix: MdPix,
  bill: FaBarcode,
  withdrawal: FaUniversity,
  internal_transfer: FaExchangeAlt,
  commission: FaHandHoldingUsd,
  fee: FaReceipt
}

const iconFor = transaction => {
  const Icon = OPERATION_ICONS[transaction.operation]
  if (Icon) return Icon

  return transaction.type === 'credit' ? FaArrowDown : FaArrowUp
}

/*
  A data que vale para o titular é quando o dinheiro se moveu. `occurredAt` só existe
  depois que a movimentação aparece no extrato do Asaas, então até lá a linha é datada
  pela criação — que é quando ele a iniciou pelo app.
*/
const dateOf = transaction => DateTime.fromISO (transaction.occurredAt ?? transaction.createdAt)

export default function Statement ({ requestConfig, canSync = true }) {
  const [loading, setLoading] = useState (true)
  const [syncing, setSyncing] = useState (false)
  const [notice, setNotice] = useState (null)
  const [loadingMore, setLoadingMore] = useState (false)
  const [transactions, setTransactions] = useState ([])
  const [meta, setMeta] = useState (null)
  const [operation, setOperation] = useState (BankingService.OPERATION_FILTERS[0])
  const [error, setError] = useState (null)

  const getTransactions = useCallback (async (page = 1, filter = operation?.value) => {
    /*
      O administrador lê a conta de outro titular apontando para ela na query, como o resto
      da área faz com `getOwnerQuery`. Sem o par, a API não sabe de quem é o extrato e
      responde "Conta bancária não encontrada".
    */
    const query = new URLSearchParams (BankingService.getOwnerQuery ())
    query.set ('page', page)
    query.set ('per_page', PER_PAGE)
    if (filter) query.set ('operation', filter)

    const { data } = await cutileiApi.get (
      `/banking_account/transactions?${query.toString ()}`, requestConfig
    )

    return data
  }, [operation, requestConfig])

  const load = useCallback (async filter => {
    try {
      setLoading (true)
      const data = await getTransactions (1, filter)

      setTransactions (data.data ?? [])
      setMeta (data.meta ?? null)
      setError (null)
    } catch (error) {
      console.log (error.response ?? error)
      setError (error.response?.data?.message ?? 'Não foi possível carregar seu extrato')
    } finally {
      setLoading (false)
    }
  }, [getTransactions])

  useEffect (() => {
    load (BankingService.OPERATION_FILTERS[0].value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleFilterChange = async (_name, selected) => {
    setOperation (selected)
    await load (selected?.value)
  }

  const loadMore = async () => {
    if (!meta || meta.current_page >= meta.last_page) return

    try {
      setLoadingMore (true)
      const data = await getTransactions (meta.current_page + 1)

      setTransactions (prev => [...prev, ...(data.data ?? [])])
      setMeta (data.meta ?? null)
    } catch (error) {
      console.log (error.response ?? error)
      setError (error.response?.data?.message ?? 'Não foi possível carregar mais movimentações')
    } finally {
      setLoadingMore (false)
    }
  }

  /*
    Reconcilia o extrato com o Asaas antes de reler. A API limita a frequência e responde
    `synced: false` quando a sincronização foi feita há pouco — o teto de requisições é
    compartilhado por todas as subcontas, então o botão não pode virar um laço.
  */
  const sync = async () => {
    try {
      setSyncing (true)
      setError (null)

      const { data } = await cutileiApi.post (
        '/banking_account/statement/sync', {}, requestConfig
      )
      await load ()

      /*
        A API recusa sincronizar de novo dentro do intervalo mínimo e explica isso em
        `message`, com `synced: false`. Descartar a resposta fazia o botão parecer quebrado:
        o titular clicava, nada mudava na tela e nada era dito — e como a resposta volta
        rápido, dava para clicar sem parar. O teto é compartilhado por todas as subcontas,
        então insistir também não adianta.
      */
      if (data && data.synced === false) setNotice (data.message ?? null)
      else setNotice (null)
    } catch (error) {
      console.log (error.response ?? error)
      setError (error.response?.data?.message ?? 'Não foi possível atualizar seu extrato')
    } finally {
      setSyncing (false)
    }
  }

  const groups = groupByDate (transactions, dateOf)
  const hasMore = meta && meta.current_page < meta.last_page

  return (
    <Card>
      <CardHeader>
        <CardTitle>Extrato</CardTitle>
      </CardHeader>
      <Row>
        <FormField stretch={2}>
          <Select
            name='operation'
            placeholder='Filtrar movimentações'
            options={BankingService.OPERATION_FILTERS}
            value={operation}
            onChange={handleFilterChange}
            onBlur={() => {}}
          />
        </FormField>
        {canSync && (
          <SecondaryButton
            label='Atualizar'
            leftIcon={<FaSyncAlt size={12} color='#FFFFFF'/>}
            loading={syncing}
            onClick={sync}
            width={130}
          />
        )}
      </Row>
      {error && <WarningText>{error}</WarningText>}

      {/* Não é erro: é a API dizendo que sincronizou há pouco e não vai de novo. */}
      {!error && notice && <Caption>{notice}</Caption>}
      {loading ? (
        <Loading style={{alignSelf: 'center', margin: '20px 0px'}}/>
      ) : transactions.length === 0 ? (
        <EmptyState>
          <FaReceipt size={26}/>
          <b>Nenhuma movimentação por aqui</b>
          <p>
            {operation?.value
              ? 'Nenhuma movimentação deste tipo no período'
              : 'Suas entradas e saídas vão aparecer nesta lista'}
          </p>
        </EmptyState>
      ) : (
        <TransactionList>
          {groups.map (group => (
            <div key={group.key}>
              <DateGroupLabel>{formatGroupDate (group.date)}</DateGroupLabel>
              {group.items.map (transaction => {
                const credit = transaction.type === 'credit'
                const Icon = iconFor (transaction)
                const settled = transaction.status === 'confirmed'

                return (
                  <TransactionRow key={transaction.id}>
                    <TransactionIcon credit={credit}>
                      <Icon size={16}/>
                    </TransactionIcon>
                    <TransactionInfo>
                      <b>{transaction.description}</b>
                      <TransactionMeta>
                        <span>{BankingService.getOperationLabel (transaction.operation)}</span>
                        {Number (transaction.fee) > 0 && (
                          <span>{`Tarifa de R$ ${Number (transaction.fee).toFixed (2).replace ('.', ',')}`}</span>
                        )}
                      </TransactionMeta>
                    </TransactionInfo>
                    <TransactionSide>
                      <AmountText value={Number (transaction.amount)} credit={credit}/>
                      {/*
                        A situação só aparece quando não é a esperada: marcar cada linha
                        concluída com um selo verde vira ruído numa lista onde a
                        esmagadora maioria está concluída.
                      */}
                      {!settled && (
                        <StatusBadge color={BankingService.getTransactionStatusColor (transaction.status)}>
                          {BankingService.getTransactionStatusLabel (transaction.status)}
                        </StatusBadge>
                      )}
                    </TransactionSide>
                  </TransactionRow>
                )
              })}
            </div>
          ))}
        </TransactionList>
      )}
      {hasMore && (
        <SecondaryButton
          label='Carregar mais'
          loading={loadingMore}
          onClick={loadMore}
          color='#606060'
          width={160}
          height={34}
          style={{alignSelf: 'center'}}
        />
      )}
      {!loading && transactions.length > 0 && meta && (
        <InfoText align='center'>
          {`${transactions.length} de ${meta.total} movimenta${meta.total === 1 ? 'ção' : 'ções'}`}
        </InfoText>
      )}
    </Card>
  )
}
